import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Play,
  FileText,
  BookOpen,
  Heart,
  Download,
  ExternalLink,
} from 'lucide-react'
import { getConfig } from '../utils/api.js'
import './Resources.css'

const YOUTUBE_VIDEOS = [
  {
    id: 'video1',
    title: 'The Easter Story - Gospel Presentation',
    description: 'A powerful visual presentation of the Easter story and the message of the Gospel.',
    thumbnail: '/resources/easter-story-thumb.jpg',
    duration: 'Watch',
    embedId: 'pYZw0dfLmLk',
  },
  {
    id: 'video3',
    title: 'Christianity Explained',
    description: 'A clear and concise explanation of what Christians believe and why it matters.',
    thumbnail: '/resources/christianity-explained-thumb.jpg',
    duration: 'Watch',
    embedId: 'NP3m1c13vj0',
  },
  {
    id: 'video4',
    title: 'Hope and New Life in Christ',
    description: 'Discover the transformational power of the resurrection and what it means for your life.',
    thumbnail: '/resources/hope-life-thumb.jpg',
    duration: 'Watch',
    embedId: '8lJa-PcEGE4',
  },
]

const PDF_RESOURCES = [
  {
    id: 'pdf1',
    title: 'The Easter Story - Complete Guide',
    description: 'A comprehensive 12-page guide covering the events of Holy Week from Palm Sunday to Resurrection Sunday.',
    fileName: 'easter-story-guide.pdf',
    fileSize: '2.4 MB',
    pages: 12,
    icon: BookOpen,
  },
  {
    id: 'pdf2',
    title: 'Understanding Salvation - For Seekers',
    description: 'A gentle introduction to Christian faith, answering common questions for those exploring belief.',
    fileName: 'understanding-salvation.pdf',
    fileSize: '1.8 MB',
    pages: 8,
    icon: Heart,
  },
  {
    id: 'pdf3',
    title: 'Family Easter Devotional',
    description: '7-day devotional guide for families to read together during the Easter season.',
    fileName: 'family-devotional.pdf',
    fileSize: '3.1 MB',
    pages: 16,
    icon: BookOpen,
  },
  {
    id: 'pdf4',
    title: 'Scripture Reading Plan - Holy Week',
    description: 'Daily scripture readings and reflections for each day of Holy Week.',
    fileName: 'holy-week-reading-plan.pdf',
    fileSize: '1.2 MB',
    pages: 6,
    icon: BookOpen,
  },
]

export default function Resources() {
  const navigate = useNavigate()
  const [churchName, setChurchName] = useState('')

  useEffect(() => {
    getConfig()
      .then(config => setChurchName(config?.church_name ?? ''))
      .catch(err => console.error('Failed to load config:', err))
  }, [])

  return (
    <main className="resources-page page-shell">
      <div className="resources-backdrop" aria-hidden="true">
        <div className="resources-glow glow-one" />
        <div className="resources-glow glow-two" />
        <div className="resources-glow glow-three" />
      </div>

      <section className="resources-shell hero-surface">
        <div className="resources-topbar">
          <button type="button" className="resources-back-btn glass-panel" onClick={() => navigate('/experience')}>
            <ArrowLeft size={18} />
            Back to Experience
          </button>

          <div className="resources-top-actions">
            <button type="button" className="resources-ghost-btn" onClick={() => navigate('/')}>
              Return Home
            </button>
            <button type="button" className="resources-primary-btn" onClick={() => navigate('/booth')}>
              Open Photo Booth
            </button>
          </div>
        </div>

        <div className="resources-hero">
          <div className="resources-hero-content">
            <h1>Explore Deeper</h1>
            <p className="resources-summary">
              Continue your journey of faith with curated resources designed to help you understand
              the profound meaning of Easter. Whether you're a seeker, a new believer, or looking to
              deepen your faith, these materials offer guidance, inspiration, and hope.
            </p>
          </div>
        </div>

        <div className="resources-grid">
          {/* YouTube Videos Section */}
          <div className="resources-section glass-panel">
            <div className="resources-section-header">
              <div className="resources-section-icon">
                <Play size={22} />
              </div>
              <div>
                <p className="section-kicker">Watch & Learn</p>
                <h2>Video Teachings</h2>
              </div>
            </div>

            <div className="videos-grid">
              {YOUTUBE_VIDEOS.map(video => (
                <div key={video.id} className="video-card">
                  {video.embedId ? (
                    <div className="video-embed">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.embedId}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="video-thumbnail">
                      <div className="video-placeholder external-link">
                        <ExternalLink size={32} />
                        <span className="video-duration">{video.duration}</span>
                      </div>
                    </div>
                  )}
                  <div className="video-content">
                    <h3>{video.title}</h3>
                    <p>{video.description}</p>
                    {video.externalUrl && (
                      <a
                        href={video.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="video-link"
                      >
                        <ExternalLink size={14} />
                        Visit Course
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* PDF Resources Section */}
          <div className="resources-section glass-panel">
            <div className="resources-section-header">
              <div className="resources-section-icon">
                <FileText size={22} />
              </div>
              <div>
                <p className="section-kicker">Read & Study</p>
                <h2>Downloadable Guides</h2>
              </div>
            </div>

            <div className="pdfs-grid">
              {PDF_RESOURCES.map(pdf => {
                const Icon = pdf.icon
                return (
                  <div key={pdf.id} className="pdf-card">
                    <div className="pdf-icon">
                      <Icon size={28} />
                    </div>
                    <div className="pdf-content">
                      <h3>{pdf.title}</h3>
                      <p>{pdf.description}</p>
                      <div className="pdf-meta">
                        <span>{pdf.pages} pages</span>
                        <span>{pdf.fileSize}</span>
                      </div>
                      <a
                        href={`/resources/${pdf.fileName}`}
                        download
                        className="pdf-download"
                      >
                        <Download size={14} />
                        Download PDF
                      </a>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Additional External Resources Section */}
          <div className="resources-section glass-panel">
            <div className="resources-section-header">
              <div className="resources-section-icon">
                <ExternalLink size={22} />
              </div>
              <div>
                <p className="section-kicker">Explore Further</p>
                <h2>Recommended Courses & Resources</h2>
              </div>
            </div>

            <div className="external-resources-grid">
              <a
                href="https://mwtb.org/collections/easter-tracts/products/what-is-easter-all-about?variant=2201206465"
                target="_blank"
                rel="noopener noreferrer"
                className="external-resource-card"
              >
                <div className="external-resource-icon">
                  <BookOpen size={24} />
                </div>
                <div className="external-resource-content">
                  <h3>What is Easter All About?</h3>
                  <p>A Gospel tract explaining the meaning of Easter and the resurrection of Jesus.</p>
                  <span className="external-resource-link">
                    <ExternalLink size={14} />
                    Visit Website
                  </span>
                </div>
              </a>

              <a
                href="https://www.christianityexplored.us/courses/christianity-explored/"
                target="_blank"
                rel="noopener noreferrer"
                className="external-resource-card"
              >
                <div className="external-resource-icon">
                  <BookOpen size={24} />
                </div>
                <div className="external-resource-content">
                  <h3>Christianity Explored</h3>
                  <p>A seven-session course for anyone who wants to think about the meaning of life and the claims of Jesus.</p>
                  <span className="external-resource-link">
                    <ExternalLink size={14} />
                    Visit Website
                  </span>
                </div>
              </a>

              <a
                href="https://www.crossway.org/articles/download-free-easter-stories-from-the-biggest-story-bible-storybook/"
                target="_blank"
                rel="noopener noreferrer"
                className="external-resource-card"
              >
                <div className="external-resource-icon">
                  <BookOpen size={24} />
                </div>
                <div className="external-resource-content">
                  <h3>Free Easter Stories from Crossway</h3>
                  <p>Download free Easter Bible stories from "The Biggest Story" Bible storybook for children and families.</p>
                  <span className="external-resource-link">
                    <ExternalLink size={14} />
                    Download Free Stories
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="resources-footer glass-panel">
          <div className="footer-content">
            <h3>Have Questions?</h3>
            <p>
              We'd love to connect with you and help you on your spiritual journey.
              Feel free to reach out to {churchName}.
            </p>
            <div className="footer-actions">
              <a href="https://thenjbc.org/#contact" className="footer-link">
                Contact Us
              </a>
              <a href="https://thenjbc.org" target="_blank" rel="noopener noreferrer" className="footer-link">
                Visit Our Church
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
